import React, { useState, useEffect, useMemo } from 'react';
import { 
  db, 
  collection, 
  onSnapshot, 
  setDoc, 
  addDoc, 
  doc, 
  serverTimestamp, 
  OperationType, 
  handleFirestoreError 
} from '../firebase';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { MultiSelectCombo } from './MultiSelectCombo';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Search, 
  Filter, 
  Plus, 
  RefreshCw, 
  X, 
  Building2, 
  MapPin, 
  Award, 
  Star, 
  Check, 
  Mail, 
  Phone, 
  User, 
  Briefcase, 
  Sparkles, 
  ArrowRight, 
  ChevronRight, 
  FileText, 
  ShieldCheck, 
  AlertTriangle,
  Zap,
  Wrench,
  Cpu,
  Compass,
  Layers,
  CheckSquare,
  Users,
  Copy,
  Download
} from 'lucide-react';
import { jsPDF } from 'jspdf';

// Subcontractor Type Definition
export interface Subcontractor {
  id: string;
  companyName: string;
  trade: string;
  certification: string[];
  region: string;
  industry: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  description: string;
  rating: number;
  reviewsCount: number;
  minContractValue: number;
  capacity: 'Available' | 'Full Capacity' | 'Flexible';
  completedProjects: number;
  createdBy: string;
  createdAt: any;
}

// Default pre-seeded subcontractors
const DEFAULT_SUBCONTRACTORS: Omit<Subcontractor, 'createdAt'>[] = [
  {
    id: 'sub_caltech_electrical',
    companyName: 'CalTech Electrical Systems',
    trade: 'Electrical',
    certification: ['DBE', 'MBE'],
    region: 'California',
    industry: 'Construction & Facilities',
    contactName: 'David Chen',
    contactEmail: 'dchen@caltechelectrical.com',
    contactPhone: '(213) 555-0192',
    description: 'Specialists in high-voltage industrial installations, smart grid integrations, solar array systems, and public facility rewiring projects with 15+ years of state-level experience.',
    rating: 4.9,
    reviewsCount: 34,
    minContractValue: 25000,
    capacity: 'Available',
    completedProjects: 28,
    createdBy: 'system'
  },
  {
    id: 'sub_lonestar_hvac',
    companyName: 'Lonestar Mechanical & HVAC',
    trade: 'Mechanical & HVAC',
    certification: ['SDVOB'],
    region: 'Texas',
    industry: 'Construction & Facilities',
    contactName: 'Marcus Vance',
    contactEmail: 'mvance@lonestarhvac.com',
    contactPhone: '(512) 555-0143',
    description: 'Full-service commercial HVAC design-build, energy-efficiency retrofitting, industrial ductwork, and smart building climate controls. Fully bonded and veteran-owned.',
    rating: 4.8,
    reviewsCount: 42,
    minContractValue: 15000,
    capacity: 'Available',
    completedProjects: 34,
    createdBy: 'system'
  },
  {
    id: 'sub_cyberguard_fed',
    companyName: 'CyberGuard Federal Solutions',
    trade: 'IT & Cybersecurity',
    certification: ['SBA 8(a)', 'HUBZone'],
    region: 'Georgia',
    industry: 'Information Technology',
    contactName: 'Elena Rostova',
    contactEmail: 'erostova@cyberguardfed.com',
    contactPhone: '(404) 555-0188',
    description: 'NIST-compliant cybersecurity engineering, federal network architecture design, penetration testing, secure cloud migrations, and compliance consulting for high-security public records.',
    rating: 5.0,
    reviewsCount: 18,
    minContractValue: 50000,
    capacity: 'Full Capacity',
    completedProjects: 15,
    createdBy: 'system'
  },
  {
    id: 'sub_vanguard_eng',
    companyName: 'Vanguard Structural Engineers',
    trade: 'Structural Engineering',
    certification: ['WBE'],
    region: 'Colorado',
    industry: 'Professional Services',
    contactName: 'Sarah Jenkins, PE',
    contactEmail: 'sjenkins@vanguardeng.com',
    contactPhone: '(303) 555-0112',
    description: 'Comprehensive civil and structural engineering consulting. Specializing in seismic retrofitting, commercial concrete frameworks, and green bridge infrastructure designs.',
    rating: 4.7,
    reviewsCount: 26,
    minContractValue: 10000,
    capacity: 'Flexible',
    completedProjects: 22,
    createdBy: 'system'
  },
  {
    id: 'sub_ecoclean_env',
    companyName: 'EcoClean Environmental Group',
    trade: 'Environmental Services',
    certification: ['MBE', 'DBE'],
    region: 'New York',
    industry: 'Environmental & Remediation',
    contactName: 'Carlos Mendez',
    contactEmail: 'cmendez@ecocleangroup.com',
    contactPhone: '(212) 555-0176',
    description: 'Hazardous waste remediation, soil testing, lead/asbestos abatement, environmental impact studies, and sustainable green roof stormwater management design.',
    rating: 4.6,
    reviewsCount: 15,
    minContractValue: 20000,
    capacity: 'Available',
    completedProjects: 19,
    createdBy: 'system'
  },
  {
    id: 'sub_empire_roofing',
    companyName: 'Empire Roofing & Waterproofing',
    trade: 'Roofing & Waterproofing',
    certification: ['WBE', 'SBA 8(a)'],
    region: 'New York',
    industry: 'Construction & Facilities',
    contactName: 'Samantha Sterling',
    contactEmail: 'ssterling@empireroofing.com',
    contactPhone: '(516) 555-0121',
    description: 'Premier commercial roofing services, cool roof installations, solar roofing, waterproofing systems, and structural deck restorations for municipal school systems.',
    rating: 4.8,
    reviewsCount: 50,
    minContractValue: 30000,
    capacity: 'Available',
    completedProjects: 45,
    createdBy: 'system'
  },
  {
    id: 'sub_palmetto_security',
    companyName: 'Palmetto Security & Fire Systems',
    trade: 'Security Systems',
    region: 'South Carolina',
    certification: ['SDVOB', 'HUBZone'],
    industry: 'Construction & Facilities',
    contactName: 'Garrett Hayes',
    contactEmail: 'ghayes@palmettosecurity.com',
    contactPhone: '(803) 555-0154',
    description: 'Integrated facility surveillance systems, CCTV networks, commercial fire alarm compliance audits, biometric access controls, and emergency response infrastructure.',
    rating: 4.9,
    reviewsCount: 11,
    minContractValue: 12000,
    capacity: 'Available',
    completedProjects: 12,
    createdBy: 'system'
  },
  {
    id: 'sub_interstate_logistics',
    companyName: 'Interstate Logistics & Supply',
    trade: 'Logistics & Supply Chain',
    region: 'Georgia',
    certification: ['DBE'],
    industry: 'Transportation & Logistics',
    contactName: 'Tyrone Washington',
    contactEmail: 'twashington@interstatelogistics.com',
    contactPhone: '(678) 555-0139',
    description: 'Intermodal freight management, heavy machinery transportation, emergency warehousing reserves, and disaster-recovery supply coordination for county governments.',
    rating: 4.5,
    reviewsCount: 56,
    minContractValue: 5000,
    capacity: 'Flexible',
    completedProjects: 50,
    createdBy: 'system'
  }
];

export const BusinessDirectoryView: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useSavedSolicitations();
  
  // State variables
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [solicitations, setSolicitations] = useState<any[]>([]);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCerts, setSelectedCerts] = useState<string[]>([]);
  const [selectedCapacities, setSelectedCapacities] = useState<string[]>([]);

  // UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subcontractor | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'all' | 'matchmaker'>('all');

  // New Subcontractor form state
  const [newSub, setNewSub] = useState({
    companyName: '',
    trade: 'Electrical',
    certification: [] as string[],
    region: 'California',
    industry: 'Construction & Facilities',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    minContractValue: '',
    capacity: 'Available' as 'Available' | 'Full Capacity' | 'Flexible',
  });
  const [savingSub, setSavingSub] = useState(false);

  // Matchmaker state
  const [selectedSolicitationId, setSelectedSolicitationId] = useState('');
  const [matchingInProgress, setMatchingInProgress] = useState(false);
  const [matchResults, setMatchResults] = useState<any | null>(null);
  const [generatedDoc, setGeneratedDoc] = useState<string>('');
  const [showDocModal, setShowDocModal] = useState(false);

  // Form selections lists
  const TRADES = [
    'Electrical', 
    'Mechanical & HVAC', 
    'Plumbing', 
    'IT & Cybersecurity', 
    'Structural Engineering', 
    'Environmental Services', 
    'Roofing & Waterproofing', 
    'Security Systems', 
    'Logistics & Supply Chain',
    'General Contracting',
    'Professional Services'
  ];

  const REGIONS = [
    'California', 'Texas', 'Georgia', 'Colorado', 'New York', 'South Carolina', 'Florida', 'Virginia', 'Illinois', 'Washington'
  ];

  const CERTIFICATIONS = [
    { code: 'MBE', name: 'Minority Business Enterprise' },
    { code: 'WBE', name: 'Women Business Enterprise' },
    { code: 'DBE', name: 'Disadvantaged Business Enterprise' },
    { code: 'SDVOB', name: 'Service-Disabled Veteran-Owned Business' },
    { code: 'HUBZone', name: 'HUBZone Certified Small Business' },
    { code: 'SBA 8(a)', name: 'SBA 8(a) Business Development Program' }
  ];

  const tradeOptions = useMemo(() => TRADES.map(t => ({ value: t, label: t })), []);
  const regionOptions = useMemo(() => REGIONS.map(r => ({ value: r, label: r })), []);
  const certOptions = useMemo(() => CERTIFICATIONS.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` })), []);
  const capacityOptions = useMemo(() => [
    { value: 'Available', label: 'Available Only' },
    { value: 'Flexible', label: 'Flexible Capacity' },
    { value: 'Full Capacity', label: 'Full Capacity' }
  ], []);

  // Fetch subcontractors and solicitations from Firestore
  useEffect(() => {
    setLoading(true);
    
    // Subscribe to Subcontractors collection
    const unsubSubs = onSnapshot(collection(db, 'subcontractors'), (snapshot) => {
      if (snapshot.empty) {
        // Seed database if empty
        seedDefaultSubcontractors();
      } else {
        const list: Subcontractor[] = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as Subcontractor);
        });
        setSubcontractors(list);
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching subcontractors:", error);
      // Fallback to local default set if firestore fails or denies
      setSubcontractors(DEFAULT_SUBCONTRACTORS as Subcontractor[]);
      setLoading(false);
    });

    // Subscribe to Solicitations to display in Matchmaker
    const unsubSols = onSnapshot(collection(db, 'solicitations'), (snapshot) => {
      const list: any[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setSolicitations(list);
    }, (error) => {
      console.error("Error fetching solicitations:", error);
    });

    return () => {
      unsubSubs();
      unsubSols();
    };
  }, []);

  // Helper to seed database
  const seedDefaultSubcontractors = async () => {
    try {
      for (const sub of DEFAULT_SUBCONTRACTORS) {
        await setDoc(doc(db, 'subcontractors', sub.id), {
          ...sub,
          createdAt: serverTimestamp()
        });
      }
      console.log("Successfully pre-seeded subcontractors directory in Firestore.");
    } catch (err) {
      console.error("Could not pre-seed subcontractors, utilizing client-side state:", err);
      setSubcontractors(DEFAULT_SUBCONTRACTORS as Subcontractor[]);
      setLoading(false);
    }
  };

  // Filter subcontractor list
  const filteredSubcontractors = useMemo(() => {
    return subcontractors.filter((sub) => {
      if (!sub) return false;
      const companyName = sub.companyName || '';
      const description = sub.description || '';
      const trade = sub.trade || '';
      const contactName = sub.contactName || '';

      const matchesSearch = 
        companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trade.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contactName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesTrade = selectedTrades.length === 0 || (sub.trade && selectedTrades.includes(sub.trade));
      const matchesRegion = selectedRegions.length === 0 || (sub.region && selectedRegions.includes(sub.region));
      const matchesCapacity = selectedCapacities.length === 0 || (sub.capacity && selectedCapacities.includes(sub.capacity));
      const matchesCert = selectedCerts.length === 0 || (Array.isArray(sub.certification) && sub.certification.some(c => selectedCerts.includes(c)));

      return matchesSearch && matchesTrade && matchesRegion && matchesCapacity && matchesCert;
    });
  }, [subcontractors, searchQuery, selectedTrades, selectedRegions, selectedCerts, selectedCapacities]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = subcontractors.length;
    const states = new Set(subcontractors.map(s => s.region).filter(Boolean)).size;
    const certified = subcontractors.filter(s => Array.isArray(s.certification) && s.certification.length > 0).length;
    const avgRating = total > 0 
      ? (subcontractors.reduce((acc, curr) => acc + (curr.rating || 0), 0) / total).toFixed(1) 
      : '0.0';

    return { total, states, certified, avgRating };
  }, [subcontractors]);

  // Toggle certification select in add form
  const handleCertCheckbox = (code: string) => {
    setNewSub(prev => {
      const exists = prev.certification.includes(code);
      if (exists) {
        return { ...prev, certification: prev.certification.filter(c => c !== code) };
      } else {
        return { ...prev, certification: [...prev.certification, code] };
      }
    });
  };

  // Add new subcontractor listing to Firestore
  const handleAddSubcontractor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSub.companyName || !newSub.contactName || !newSub.contactEmail) {
      alert("Please populate all required fields.");
      return;
    }

    setSavingSub(true);
    const subId = `sub_${Date.now()}`;
    const payload: Subcontractor = {
      id: subId,
      companyName: newSub.companyName,
      trade: newSub.trade,
      certification: newSub.certification,
      region: newSub.region,
      industry: newSub.industry,
      contactName: newSub.contactName,
      contactEmail: newSub.contactEmail,
      contactPhone: newSub.contactPhone || '(N/A)',
      description: newSub.description || 'No company description provided.',
      rating: parseFloat((4.5 + Math.random() * 0.5).toFixed(1)), // Generates 4.5 - 5.0
      reviewsCount: Math.floor(1 + Math.random() * 15),
      minContractValue: Number(newSub.minContractValue) || 10000,
      capacity: newSub.capacity,
      completedProjects: Math.floor(5 + Math.random() * 20),
      createdBy: user?.uid || 'anonymous',
      createdAt: new Date().toISOString() // Fallback string representation
    };

    try {
      await setDoc(doc(db, 'subcontractors', subId), {
        ...payload,
        createdAt: serverTimestamp()
      });
      setShowAddModal(false);
      // Reset form
      setNewSub({
        companyName: '',
        trade: 'Electrical',
        certification: [],
        region: 'California',
        industry: 'Construction & Facilities',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        description: '',
        minContractValue: '',
        capacity: 'Available',
      });
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, `subcontractors/${subId}`);
    } finally {
      setSavingSub(false);
    }
  };

  // Get matching trade icon helper
  const getTradeIcon = (trade: string) => {
    switch (trade) {
      case 'Electrical': return <Zap className="w-5 h-5 text-amber-500" />;
      case 'Mechanical & HVAC': return <Compass className="w-5 h-5 text-sky-500" />;
      case 'Plumbing': return <Wrench className="w-5 h-5 text-blue-500" />;
      case 'IT & Cybersecurity': return <Cpu className="w-5 h-5 text-indigo-500" />;
      case 'Structural Engineering': return <Layers className="w-5 h-5 text-emerald-500" />;
      case 'Security Systems': return <Users className="w-5 h-5 text-rose-500" />;
      default: return <Building2 className="w-5 h-5 text-gray-500" />;
    }
  };

  // AI Matching subcontractor logic
  const handleMatchmakingAnalysis = async () => {
    if (!selectedSolicitationId) return;
    setMatchingInProgress(true);
    setMatchResults(null);

    const solicitation = solicitations.find(s => s.solicitationId === selectedSolicitationId);
    if (!solicitation) {
      setMatchingInProgress(false);
      return;
    }

    try {
      // API request to match subcontractors
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            {
              role: 'user',
              content: `Analyze the following public sector Solicitation/RFP and suggest the ideal subcontracting partners from our business directory. 
              
              === SOLICITATION RFP ===
              ID: ${solicitation.solicitationId}
              Title: ${solicitation.title}
              Agency: ${solicitation.agency || solicitation.buyingagency || 'N/A'}
              Region/State: ${solicitation.state}
              Category: ${solicitation.category || solicitation.maincommodity || 'General'}
              Description: ${solicitation.description}
              
              === AVAILABLE SUBCONTRACTORS ===
              ${JSON.stringify(subcontractors.map(s => ({
                id: s.id,
                name: s.companyName,
                trade: s.trade,
                certs: s.certification,
                region: s.region,
                desc: s.description,
                rating: s.rating,
                capacity: s.capacity
              })))}

              Your output MUST be a strict JSON structure containing matching analysis. 
              Do not add markdown formatting outside the JSON block. Return exactly this JSON schema:
              {
                "matches": [
                  {
                    "subcontractorId": "exact_subcontractor_id_from_above",
                    "matchScore": 95, 
                    "matchingReasons": ["list of 3 precise reasons highlighting trade alignment, local region match, or diversity certifications boosting bid scoring"],
                    "roleProposal": "detailed description of what parts of the RFP this subcontractor handles"
                  }
                ],
                "strategicAdvisory": "A detailed 2-3 paragraph strategy advisory explaining how a small business partnering with these entities satisfies preference programs, strengthens technical compliance, and expands general capacity to pursue this larger contract successfully."
              }`
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error("Matchmaking API failed");
      }

      const resData = await response.json();
      let parsedResults;
      try {
        // Clean markdown block wrappers if present
        let rawText = resData.text.trim();
        if (rawText.startsWith('```json')) {
          rawText = rawText.substring(7);
        }
        if (rawText.endsWith('```')) {
          rawText = rawText.substring(0, rawText.length - 3);
        }
        parsedResults = JSON.parse(rawText.trim());
      } catch (e) {
        console.error("JSON parse failed for matchmaking response:", e);
        throw new Error("Invalid matchmaking response format");
      }

      setMatchResults(parsedResults);
    } catch (err) {
      console.error("Matchmaking error:", err);
      const fallback = fallbackMatchSubcontractors(solicitation);
      setMatchResults(fallback);
      addToast?.({
        title: 'Offline Matchmaking Active',
        message: 'Using heuristic business directory matching algorithm.',
        type: 'info'
      });
    } finally {
      setMatchingInProgress(false);
    }
  };

  const fallbackMatchSubcontractors = (solicitation: any) => {
    const solCategory = (solicitation.category || solicitation.maincommodity || '').toLowerCase();
    const solState = (solicitation.state || '').toLowerCase();
    
    // Attempt matching by categories and states
    const matches = subcontractors.map(sub => {
      let score = 50;
      const reasons: string[] = [];
      let role = `Provide secondary technical assistance on tasks aligned to ${sub.trade}.`;

      // Trade/Category Match
      if (solCategory.includes('it') || solCategory.includes('tech') || solCategory.includes('software')) {
        if (sub.trade === 'IT & Cybersecurity') {
          score += 40;
          reasons.push("Direct technology and systems alignment.");
          role = "Lead cyber security implementation, threat vector modeling, and NIST system hardening compliance.";
        }
      } else if (solCategory.includes('construct') || solCategory.includes('maintenance') || solCategory.includes('rehab') || solCategory.includes('highway') || solCategory.includes('roof')) {
        if (sub.trade === 'Electrical') {
          score += 35;
          reasons.push("High demand for certified low/high voltage technical infrastructure.");
          role = "Coordinate state-compliant facility wiring, main breaker panels installation, and certified safety grounding.";
        }
        if (sub.trade === 'Mechanical & HVAC') {
          score += 35;
          reasons.push("Heavy-duty ventilation systems installation and energy auditing alignment.");
          role = "Install variable air volume boxes, structural roof-mounted ventilation units, and state agency environmental automation.";
        }
        if (sub.trade === 'Roofing & Waterproofing') {
          score += 40;
          reasons.push("Primary building shell thermal insulation and waterproofing compliance.");
          role = "Implement dynamic membrane flashing, drainage optimization, and commercial multi-ply roof installation.";
        }
      } else if (solCategory.includes('environment') || solCategory.includes('remediation') || solCategory.includes('waste')) {
        if (sub.trade === 'Environmental Services') {
          score += 45;
          reasons.push("Niche technical capability matching remediation compliance specifications.");
          role = "Direct green stormwater drainage structures development, perform toxic lead abatement, and complete statutory state environmental impact audits.";
        }
      }

      // Region match
      if (sub.region.toLowerCase() === solState) {
        score += 10;
        reasons.push(`Local operations: Headquartered directly in state of solicitation (${sub.region}).`);
      } else {
        reasons.push(`Flexible deployment: Holds multi-state licensing available for ${solicitation.state || 'regional'} contracts.`);
      }

      // Certifications boost
      if (sub.certification.length > 0) {
        score += 5;
        reasons.push(`Diversity advantage: Leveraging ${sub.certification.join('/')} partner satisfies federal and state set-aside goals.`);
      }

      // Capacity buffer
      if (sub.capacity === 'Available') {
        score += 5;
      }

      // Cap at 98
      score = Math.min(score, 98);

      return {
        subcontractorId: sub.id,
        matchScore: score,
        matchingReasons: reasons.length > 0 ? reasons : ["Certified specialist matching standard procurement delivery goals."],
        roleProposal: role
      };
    }).sort((a, b) => b.matchScore - a.matchScore).slice(0, 3);

    return {
      matches,
      strategicAdvisory: `[INTELLIGENT DUAL PARTNERSHIP STRATEGY] Partnering with the recommended subcontractors directly maximizes technical capability scores while ensuring compliance with state and county diversity mandates. Specifically, adding certified MBE/WBE or veteran-owned partners guarantees up to 10% bonus evaluation points in structured state proposals. Structuring a Joint Venture or direct subcontracting agreement allows your small business to claim massive combined past performance, offsetting capacity constraints and satisfying large bonding minimums successfully.`
    };
  };

  // Generate joint venture/subcontract agreement draft text
  const handleGenerateAgreement = (sub: Subcontractor, sol: any) => {
    const todayStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const text = `# TEAVING AGREEMENT / MEMORANDUM OF UNDERSTANDING

**DATE OF AGREEMENT**: ${todayStr}
**PRIMARY CONTRACTOR (Bidder)**: [Your Company Name]
**PARTNER/SUBCONTRACTOR**: ${sub.companyName}
**TARGET SOLICITATION**: ${sol.title} (ID: ${sol.solicitationId})
**ISSUING PUBLIC AGENCY**: ${sol.agency || sol.buyingagency || 'State Purchasing Board'}

---

### 1. RECITALS
WHEREAS, the Primary Contractor intends to submit a formal responsive proposal for the above-referenced Solicitation RFP issued by the Issuing Public Agency; and

WHEREAS, ${sub.companyName} possesses specialized expertise in **${sub.trade}** holding active certifications including **${sub.certification.join(', ') || 'Small Business Standard'}** and offers unique localized experience in **${sub.region}**; and

WHEREAS, the Parties desire to combine their capabilities in a prime-subcontractor relationship to submit a highly competitive bid and execute the contract successfully upon award.

### 2. SCOPE OF COLLABORATION
The Parties agree that ${sub.companyName} shall act as a designated subcontractor. The proposed work to be allocated includes:
- **Primary Technical Domain**: Lead all activities matching ${sub.trade} capabilities.
- **Compliance Oversight**: Ensure all deliverables comply with the regulatory programs and certifications asserted in the joint bid.
- **Reporting & Auditing**: Maintain weekly performance records and supply structural reports to the Primary Contractor.

### 3. ALLOCATION OF COMPENSABLE VALUES
Compensation shall be negotiated in good faith upon formal award of the prime contract, matching:
- A targeted **${sub.trade === 'IT & Cybersecurity' ? '30%' : '20%'}** of the total gross contract value awarded, or
- Standard professional labor rates as detailed in the final Cost Proposal sheet.

### 4. DIVERSITY & CERTIFICATION COMPLIANCE
The Subcontractor warrants that all diversity credentials (${sub.certification.join(', ') || 'SBE'}) are active and in good standing with state/federal agencies and will supply any necessary affidavits or certificates to support the Primary Contractor's compliance filing package.

---
**IN WITNESS WHEREOF**, the Parties hereto have executed this teaming agreement draft on the date first written above.

**For Primary Contractor:**
Authorized Signature: _______________________
Title: Principal Bid Director

**For ${sub.companyName}:**
Authorized Signature: _______________________
Title: ${sub.contactName}, Contact Officer`;

    setGeneratedDoc(text);
    setShowDocModal(true);
  };

  const handleGenerateTeamingDoc = (sub: Subcontractor) => {
    const targetSol = {
      title: "General Procurement Opportunity",
      solicitationId: "STATE-PROC-MATCH",
      agency: "Public Contracting Agency",
      state: sub.region
    };
    handleGenerateAgreement(sub, targetSol);
  };

  // Export Teaming Agreement to PDF
  const handleExportPDF = (subName: string) => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "normal");
    
    // Title
    doc.setFontSize(18);
    doc.text("TEAMING COLLABORATION AGREEMENT DRAFT", 20, 20);
    
    doc.setFontSize(10);
    doc.text(`Exported from RFXpress Portal on ${new Date().toLocaleDateString()}`, 20, 28);
    
    doc.line(20, 32, 190, 32);
    
    // Body text wrapping
    doc.setFontSize(11);
    const splitText = doc.splitTextToSize(generatedDoc, 170);
    doc.text(splitText, 20, 42);
    
    doc.save(`Teaming_Agreement_${subName.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div id="business-directory-root" className="space-y-8">
      {/* Upper header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <Building2 className="text-indigo-600 w-8 h-8" />
            Partner Business Directory
          </h1>
          <p className="text-gray-500 mt-1 max-w-2xl">
            Browse qualified subcontractors by trade, certification, or region to meet RFP diversity quotas, fulfill technical requirements, and pursue high-value public contracts.
          </p>
        </div>
        <div className="flex items-center gap-3 self-start md:self-center">
          <div className="bg-gray-100 p-1 rounded-xl flex">
            <button
              onClick={() => setActiveSubTab('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeSubTab === 'all' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              All Subcontractors
            </button>
            <button
              onClick={() => setActiveSubTab('matchmaker')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                activeSubTab === 'matchmaker' 
                  ? 'bg-white text-indigo-700 shadow-sm' 
                  : 'text-gray-500 hover:text-indigo-700'
              }`}
            >
              <Sparkles className="w-4 h-4 text-indigo-500" />
              AI Matchmaker
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-sm shadow-indigo-100 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Add Subcontractor
          </button>
        </div>
      </div>

      {/* Stats Widgets Panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Subcontractors Listed</p>
            <p className="text-2xl font-black text-gray-900">{stats.total}</p>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <MapPin className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Regions Represented</p>
            <p className="text-2xl font-black text-gray-900">{stats.states}</p>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <Award className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Certified Diverse Partners</p>
            <p className="text-2xl font-black text-gray-900">{stats.certified}</p>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500">
            <Star className="w-6 h-6 fill-amber-500" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Avg Performance Rating</p>
            <p className="text-2xl font-black text-gray-900">{stats.avgRating} <span className="text-xs font-normal text-gray-400">/ 5.0</span></p>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeSubTab === 'all' ? (
          <motion.div
            key="directory-all"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
          >
            {/* Filter Controls Panel */}
            <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm space-y-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by company name, keywords, trade or details..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-600 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm transition-all outline-none"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-center">
                  {/* Trade filter */}
                  <div>
                    <MultiSelectCombo
                      options={tradeOptions}
                      selectedValues={selectedTrades}
                      onChange={setSelectedTrades}
                      placeholder="All Trades"
                      icon={<Briefcase className="w-3.5 h-3.5" />}
                    />
                  </div>

                  {/* Region filter */}
                  <div>
                    <MultiSelectCombo
                      options={regionOptions}
                      selectedValues={selectedRegions}
                      onChange={setSelectedRegions}
                      placeholder="All Regions"
                      icon={<MapPin className="w-3.5 h-3.5" />}
                    />
                  </div>

                  {/* Certification filter */}
                  <div>
                    <MultiSelectCombo
                      options={certOptions}
                      selectedValues={selectedCerts}
                      onChange={setSelectedCerts}
                      placeholder="All Certifications"
                      icon={<Award className="w-3.5 h-3.5" />}
                    />
                  </div>

                  {/* Capacity Filter */}
                  <div>
                    <MultiSelectCombo
                      options={capacityOptions}
                      selectedValues={selectedCapacities}
                      onChange={setSelectedCapacities}
                      placeholder="All Capacities"
                      icon={<RefreshCw className="w-3.5 h-3.5" />}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Subcontractor High-Density Table */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-3 bg-white rounded-2xl border border-gray-150">
                <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                <p className="text-sm text-gray-500 font-medium">Loading subcontractors database...</p>
              </div>
            ) : filteredSubcontractors.length === 0 ? (
              <div className="bg-white text-center py-16 px-4 rounded-2xl border border-gray-150 shadow-sm space-y-4">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto text-gray-400">
                  <Building2 className="w-8 h-8" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-gray-900">No Subcontractors Found</h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto">
                    No active subcontractors match your search or filter configuration. Try expanding your criteria or add a new subcontractor profile.
                  </p>
                </div>
                <button
                  onClick={() => { setSearchQuery(''); setSelectedTrades([]); setSelectedRegions([]); setSelectedCerts([]); setSelectedCapacities([]); }}
                  className="text-indigo-600 font-semibold text-sm hover:text-indigo-700"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        <th className="py-3 px-4">Company &amp; Trade</th>
                        <th className="py-3 px-3">Certifications</th>
                        <th className="py-3 px-3">Region</th>
                        <th className="py-3 px-3">Capacity</th>
                        <th className="py-3 px-3">Min Contract</th>
                        <th className="py-3 px-3">Rating / Projects</th>
                        <th className="py-3 px-3">Contact</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredSubcontractors.map((sub) => (
                        <tr 
                          key={sub.id} 
                          onClick={() => setSelectedSub(sub)}
                          className="hover:bg-indigo-50/40 transition-colors cursor-pointer group"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center shrink-0">
                                {getTradeIcon(sub.trade)}
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 group-hover:text-indigo-600 truncate max-w-[200px]">
                                  {sub.companyName}
                                </p>
                                <p className="text-[11px] text-indigo-600 font-medium truncate">
                                  {sub.trade}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                              {sub.certification.length > 0 ? (
                                sub.certification.map(cert => (
                                  <span key={cert} className="bg-purple-50 text-purple-700 text-[9px] font-bold px-1.5 py-0.5 rounded border border-purple-100">
                                    {cert}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 text-[10px] italic">None</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-600 font-medium">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="truncate max-w-[110px]">{sub.region}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase border tracking-wider ${
                              sub.capacity === 'Available' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                              sub.capacity === 'Flexible' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                              'bg-rose-50 text-rose-700 border-rose-100'
                            }`}>
                              {sub.capacity}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-mono font-bold text-slate-800">
                            ${sub.minContractValue.toLocaleString()}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
                              <span className="font-bold text-slate-800">{sub.rating}</span>
                              <span className="text-[10px] text-slate-400">({sub.completedProjects} done)</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-[11px] text-slate-600">
                            <p className="font-medium text-slate-800 truncate max-w-[130px]">{sub.contactName}</p>
                            <p className="text-slate-400 text-[10px] truncate max-w-[130px]">{sub.contactEmail}</p>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenerateTeamingDoc(sub);
                                }}
                                className="px-2.5 py-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-all cursor-pointer inline-flex items-center gap-1"
                                title="Draft Teaming Agreement"
                              >
                                <FileText className="w-3 h-3" /> Teaming Draft
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSub(sub);
                                }}
                                className="px-2 py-1 text-[10px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                              >
                                Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-slate-50/80 px-4 py-2 border-t border-gray-150 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Showing <strong>{filteredSubcontractors.length}</strong> partners in registry</span>
                  <span>Click any row to inspect complete credentials, capabilities &amp; compliance history</span>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="directory-matchmaker"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
          >
            {/* Matchmaker Interactive Panel */}
            <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-8 rounded-3xl border border-indigo-950 shadow-xl space-y-6">
              <div className="max-w-2xl space-y-2">
                <span className="inline-flex items-center gap-1.5 bg-indigo-500/20 border border-indigo-400/30 px-3 py-1 rounded-full text-xs font-semibold text-indigo-200">
                  <Sparkles className="w-3.5 h-3.5" />
                  Gemini-Powered Partnership Matchmaker
                </span>
                <h2 className="text-2xl font-black tracking-tight">AI Subcontractor Matchmaker</h2>
                <p className="text-indigo-200/80 text-sm leading-relaxed">
                  Select a live solicitation RFP. Our AI engine will inspect the technical Statement of Work, compliance thresholds, and local region requirements, and identify the top subcontractors in the directory to complete your bid team.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
                <div className="flex-1">
                  <select
                    value={selectedSolicitationId}
                    onChange={(e) => setSelectedSolicitationId(e.target.value)}
                    className="w-full bg-slate-800/80 border border-slate-700/80 rounded-xl px-4 py-3 text-sm font-medium text-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-slate-800 cursor-pointer"
                  >
                    <option value="">-- Choose an RFX Opportunity --</option>
                    {solicitations.map(s => (
                      <option key={s.solicitationId} value={s.solicitationId}>
                        [{s.state}] {s.title.substring(0, 70)}...
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleMatchmakingAnalysis}
                  disabled={!selectedSolicitationId || matchingInProgress}
                  className="bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-800 disabled:text-slate-500 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer shadow-lg shadow-indigo-950/40"
                >
                  {matchingInProgress ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Matching Bid Partners...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Find Best Match
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Match results dashboard */}
            {matchingInProgress && (
              <div className="bg-white p-12 rounded-2xl border border-gray-150 shadow-sm flex flex-col items-center justify-center text-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600 animate-pulse">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <div className="absolute inset-0 border-2 border-indigo-100 rounded-full animate-ping opacity-75" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-bold text-gray-900">Conducting Strategic Bid Synthesis...</h3>
                  <p className="text-xs text-gray-500 max-w-sm">
                    Gemini is reviewing the SOW, mapping Naics classifications, and verifying active diversity certification benefits.
                  </p>
                </div>
              </div>
            )}

            {matchResults && !matchingInProgress && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Match List column */}
                <div className="lg:col-span-2 space-y-6">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                    Recommended Joint-Venture Partners
                  </h3>

                  <div className="space-y-4">
                    {matchResults.matches?.map((match: any, idx: number) => {
                      const sub = subcontractors.find(s => s.id === match.subcontractorId);
                      if (!sub) return null;
                      return (
                        <div 
                          key={match.subcontractorId}
                          className="bg-white border border-gray-150 p-6 rounded-2xl shadow-sm space-y-4 hover:border-indigo-200 transition-all"
                        >
                          {/* Match Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                                {getTradeIcon(sub.trade)}
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                                  {sub.companyName}
                                  <span className="text-[10px] bg-indigo-100 text-indigo-800 font-bold px-2 py-0.5 rounded uppercase">
                                    {sub.trade}
                                  </span>
                                </h4>
                                <p className="text-xs text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {sub.region} &bull; Rating {sub.rating} ★
                                </p>
                              </div>
                            </div>

                            {/* Score badge */}
                            <div className="text-right shrink-0">
                              <span className={`text-base font-black px-3 py-1 rounded-xl flex items-center gap-1 ${
                                match.matchScore >= 90 ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                                'bg-indigo-50 text-indigo-700 border border-indigo-100'
                              }`}>
                                {match.matchScore}% <span className="text-[10px] font-normal text-gray-400">match</span>
                              </span>
                            </div>
                          </div>

                          {/* Allocation proposal */}
                          <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-100/60 space-y-1">
                            <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider">Proposed SOW Responsibilities</p>
                            <p className="text-xs text-gray-600 font-medium leading-relaxed">
                              {match.roleProposal}
                            </p>
                          </div>

                          {/* Matching Reasons bullet checklist */}
                          <div className="space-y-2">
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Strategic Match Synergy</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {match.matchingReasons?.map((reason: string, rIdx: number) => (
                                <div key={rIdx} className="flex items-start gap-2 text-xs text-gray-600">
                                  <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                  <span>{reason}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-50">
                            <button
                              onClick={() => setSelectedSub(sub)}
                              className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:text-gray-900 text-xs font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              Partner Profile
                            </button>
                            <button
                              onClick={() => {
                                const solicitation = solicitations.find(s => s.solicitationId === selectedSolicitationId);
                                if (solicitation) {
                                  handleGenerateAgreement(sub, solicitation);
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Draft Teaming Agreement
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Strategy Advisory Column */}
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-600" />
                    Strategic Advisory
                  </h3>

                  <div className="bg-white border border-gray-150 p-6 rounded-2xl shadow-sm space-y-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <Award className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">Winning Strategy Analysis</h4>
                    
                    <p className="text-xs text-gray-500 leading-relaxed whitespace-pre-line">
                      {matchResults.strategicAdvisory}
                    </p>

                    <div className="pt-4 border-t border-gray-50 space-y-3 text-xs text-gray-600">
                      <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                          Remember that most state procurement guidelines mandate a minimum 25% small or diverse business participation quota. Leverage these partnerships heavily in Section 4.0 of your bidding document.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!matchResults && !matchingInProgress && (
              <div className="bg-white p-12 text-center rounded-2xl border border-gray-150 shadow-sm space-y-3">
                <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 mx-auto">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-gray-900">Select a Solicitation</h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Choose an active procurement opportunity from the dropdown above to execute our intelligent partner matching assessment.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Drawer (Subcontractor profile modal) */}
      <AnimatePresence>
        {selectedSub && (
          <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedSub(null)}
              className="absolute inset-0 bg-black cursor-pointer"
            />
            
            {/* Drawer Body */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col justify-between overflow-y-auto"
            >
              <div>
                {/* Header Banner */}
                <div className="bg-slate-900 text-white p-6 relative">
                  <button 
                    onClick={() => setSelectedSub(null)}
                    className="absolute top-4 right-4 bg-slate-800 hover:bg-slate-700 text-gray-300 hover:text-white p-1.5 rounded-lg transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="space-y-3">
                    <span className="bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded">
                      {selectedSub.trade}
                    </span>
                    <h3 className="text-xl font-extrabold tracking-tight">{selectedSub.companyName}</h3>
                    <p className="text-slate-300 text-xs flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {selectedSub.region} &bull; {selectedSub.industry}
                    </p>
                  </div>
                </div>

                {/* Subcontractor details */}
                <div className="p-6 space-y-6">
                  {/* Performance stats */}
                  <div className="grid grid-cols-3 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Rating</p>
                      <p className="text-lg font-black text-gray-900 mt-1 flex items-center justify-center gap-1 text-amber-500">
                        <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                        {selectedSub.rating}
                      </p>
                    </div>
                    <div className="text-center border-x border-gray-200">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Completed</p>
                      <p className="text-lg font-black text-gray-900 mt-1">
                        {selectedSub.completedProjects} <span className="text-xs font-medium text-gray-400">jobs</span>
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Capacity</p>
                      <p className={`text-xs font-extrabold uppercase mt-2 tracking-wide ${
                        selectedSub.capacity === 'Available' ? 'text-emerald-600' :
                        selectedSub.capacity === 'Flexible' ? 'text-amber-600' :
                        'text-rose-600'
                      }`}>
                        {selectedSub.capacity}
                      </p>
                    </div>
                  </div>

                  {/* Company Overview */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Company Overview</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {selectedSub.description}
                    </p>
                  </div>

                  {/* Diversity Certifications */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Diversity Certifications</h4>
                    {selectedSub.certification.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedSub.certification.map(cert => {
                          const detailedName = CERTIFICATIONS.find(c => c.code === cert)?.name || 'State Certified Partner';
                          return (
                            <div 
                              key={cert}
                              className="bg-purple-50 text-purple-700 px-3 py-1.5 rounded-xl border border-purple-100/50 flex items-center gap-1.5 text-xs font-semibold"
                            >
                              <Award className="w-4 h-4 shrink-0 text-purple-500" />
                              <span>{cert} <span className="text-[10px] text-purple-400 font-normal">({detailedName})</span></span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No special diversity certifications declared.</p>
                    )}
                  </div>

                  {/* Direct Contact */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Direct Engagement Contact</h4>
                    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-2.5 text-xs text-slate-600">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-bold text-slate-900">{selectedSub.contactName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <a href={`mailto:${selectedSub.contactEmail}`} className="text-indigo-600 font-medium hover:underline">
                          {selectedSub.contactEmail}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedSub.contactPhone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Drawer engagement button */}
              <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-3">
                <a 
                  href={`mailto:${selectedSub.contactEmail}?subject=Partnership Invitation for Public Solicitation`}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-center text-sm py-3 rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Mail className="w-4 h-4" />
                  Email Partner
                </a>
                <button
                  onClick={() => setSelectedSub(null)}
                  className="px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-100 text-gray-600 hover:text-gray-900 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Subcontractor Listing Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto px-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden z-10">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-slate-900 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-400" />
                Register New Subcontractor
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-300 hover:text-white cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleAddSubcontractor} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Company Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Paramount Civil Engineers"
                    value={newSub.companyName}
                    onChange={(e) => setNewSub(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Primary Trade *</label>
                  <select
                    value={newSub.trade}
                    onChange={(e) => setNewSub(prev => ({ ...prev, trade: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all cursor-pointer"
                  >
                    {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">State/Region *</label>
                  <select
                    value={newSub.region}
                    onChange={(e) => setNewSub(prev => ({ ...prev, region: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all cursor-pointer"
                  >
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Capacity</label>
                  <select
                    value={newSub.capacity}
                    onChange={(e) => setNewSub(prev => ({ ...prev, capacity: e.target.value as any }))}
                    className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all cursor-pointer"
                  >
                    <option value="Available">Available</option>
                    <option value="Flexible">Flexible Capacity</option>
                    <option value="Full Capacity">Full Capacity</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Min Job Value ($)</label>
                  <input
                    type="number"
                    placeholder="e.g. 10000"
                    value={newSub.minContractValue}
                    onChange={(e) => setNewSub(prev => ({ ...prev, minContractValue: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all"
                  />
                </div>
              </div>

              {/* Certifications Checkbox list */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase">Diversity Certifications</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-xl border border-gray-150">
                  {CERTIFICATIONS.map(c => (
                    <label key={c.code} className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={newSub.certification.includes(c.code)}
                        onChange={() => handleCertCheckbox(c.code)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span title={c.name}>{c.code}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Direct Contacts */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                <div className="col-span-2 md:col-span-1 space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Contact Representative Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="John Doe"
                    value={newSub.contactName}
                    onChange={(e) => setNewSub(prev => ({ ...prev, contactName: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all"
                  />
                </div>

                <div className="col-span-2 md:col-span-1 space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Contact Representative Email *</label>
                  <input
                    type="email"
                    required
                    placeholder="jdoe@company.com"
                    value={newSub.contactEmail}
                    onChange={(e) => setNewSub(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all"
                  />
                </div>

                <div className="col-span-2 space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">Contact phone</label>
                  <input
                    type="text"
                    placeholder="(555) 012-3456"
                    value={newSub.contactPhone}
                    onChange={(e) => setNewSub(prev => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Description / Scope Details</label>
                <textarea
                  rows={3}
                  placeholder="Tell us about your technical specialties, previous completed state/municipal bids, and capability thresholds..."
                  value={newSub.description}
                  onChange={(e) => setNewSub(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-gray-50 hover:bg-gray-100/50 focus:bg-white focus:ring-2 focus:ring-indigo-100 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all resize-none"
                />
              </div>

              {/* Actions footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-100 text-gray-600 hover:text-gray-900 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSub}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-semibold text-sm px-5 py-2.5 rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  {savingSub ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving Listing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Register Listing
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generated MOU Teaming Agreement Modal */}
      {showDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto px-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowDocModal(false)} />
          <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden z-10 flex flex-col justify-between max-h-[85vh]">
            <div className="px-6 py-4 bg-slate-900 text-white border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-base font-bold flex items-center gap-2">
                <FileText className="text-indigo-400 w-5 h-5" />
                Collaborative Teaming Agreement Draft
              </h3>
              <button onClick={() => setShowDocModal(false)} className="text-gray-300 hover:text-white cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto bg-gray-50 text-xs font-mono text-gray-700 whitespace-pre-wrap leading-relaxed select-text select-all">
              {generatedDoc}
            </div>

            <div className="p-6 border-t border-gray-100 bg-white flex items-center justify-between gap-3 shrink-0">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generatedDoc);
                  alert("Teaming agreement copied to clipboard successfully!");
                }}
                className="px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-100 text-gray-700 font-semibold text-xs flex items-center gap-1.5 cursor-pointer transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copy Draft Text
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExportPDF('Partner_Collaboration')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs px-4 py-2.5 rounded-xl flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download PDF Draft
                </button>
                <button
                  onClick={() => setShowDocModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:text-gray-900 font-semibold text-xs transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
